import { createContext, createElement, useContext, useEffect, useState, type ReactNode } from "react";

// Mirrors GET /v1/web/session. A visitor is also what a pending, failed, or
// malformed read looks like: the nav never waits on this and never breaks.
export type Session =
  | { kind: "visitor" }
  | { kind: "member" | "admin"; username: string; emailVerified: boolean; impersonating: boolean };

export const VISITOR: Session = { kind: "visitor" };

const SESSION_URL = "/v1/web/session";

// Keep only known fields, so nothing extra in the payload (an email, say) can
// reach state or the DOM.
export function normalizeSession(raw: unknown): Session {
  if (raw === null || typeof raw !== "object") return VISITOR;
  const body = raw as Record<string, unknown>;
  if (body.logged_in !== true || typeof body.username !== "string" || body.username === "") {
    return VISITOR;
  }
  return {
    kind: body.is_admin === true ? "admin" : "member",
    username: body.username,
    emailVerified: body.email_verified === true,
    // An admin signed in as this user; the nav offers the way back.
    impersonating: body.impersonating === true,
  };
}

const SessionContext = createContext<Session>(VISITOR);
// False until the first read settles, so a page that needs a member can tell
// "not signed in" from "not known yet".
const SessionReadyContext = createContext<boolean>(false);

// One read per mount. Mounted above the router's Outlet, so moving between app
// routes does not refetch.
export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session>(VISITOR);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const ctrl = new AbortController();
    fetch(SESSION_URL, {
      method: "GET",
      credentials: "same-origin",
      signal: ctrl.signal,
      headers: { Accept: "application/json" },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`session ${res.status}`))))
      .then((raw) => setSession(normalizeSession(raw)))
      .catch(() => {
        // A failed read leaves the visitor view in place.
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setReady(true);
      });
    return () => ctrl.abort();
  }, []);
  return createElement(
    SessionReadyContext.Provider,
    { value: ready },
    createElement(SessionContext.Provider, { value: session }, children),
  );
}

export function useSession(): Session {
  return useContext(SessionContext);
}

export function useSessionReady(): boolean {
  return useContext(SessionReadyContext);
}
